// Angular Imports
// =========================================================
import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { BehaviorSubject, Subject } from 'rxjs'
// Amplify Imports
// =========================================================
import { Hub, Cache, ConsoleLogger as Logger } from 'aws-amplify/utils'
import {
  getCurrentUser,
  fetchAuthSession,
  fetchUserAttributes,
  signOut,
  signInWithRedirect,
  updateUserAttributes
} from 'aws-amplify/auth'
// Custom Imports
// =========================================================

@Injectable({
  providedIn: 'root'
})
export class CognitoService {
  private readonly authenticationSubject: BehaviorSubject<any>
  private readonly triggerUserUpdate = new Subject<any>()
  public triggerUserUpdate$ = this.triggerUserUpdate.asObservable()

  loading: boolean = true
  isUserIdle: boolean = false
  tokenExpiration: number
  tempFlag: boolean = true
  user: any
  callbackUrl: string
  // Track Auth State
  logger = new Logger('Logger', 'INFO')

  constructor (private readonly router: Router) {
    this.authenticationSubject = new BehaviorSubject<boolean>(false)
  }

  public hubListener () {
    // Listen for authentication updates
    Hub.listen('auth', async ({ payload }: any) => {
      const { event } = payload
      switch (event) {
        case 'signedIn':
        case 'signInWithRedirect':
          if (!this.user) {
            this.tempFlag = false
            this.checkUser()
          }
          this.logger.info('user signed in')
          this.router.navigateByUrl('/size-intelligence-modules-dashboard')
          break
        case 'signIn_failure':
        case 'signInWithRedirect_failure':
          this.logger.info('user sign in failed')
          break
        case 'signedOut':
          this.logger.info('user signed out')
          this.user = null
          break
        case 'tokenRefresh':
          console.info('token refresh succeeded')
          break
        case 'tokenRefresh_failure':
          console.error('token refresh failed')
          this.signOut()
          break
        default:
          this.logger.error('Something went wrong, look at data object', event)
      }
    })
  }

  checkUser (test?: boolean) {
    getCurrentUser()
      .then(async () => {
        const attributes = await fetchUserAttributes()
        this.user = attributes
        console.log('Checking for user in cognito service: ', attributes)
        return this.triggerUserUpdate.next(attributes)
      })
      .catch(async () => {
        this.user = null
        console.log('Hub listener - No user found')
        return this.triggerUserUpdate.next(null)
      })
  }

  // Sign out of the application
  public async signOut (): Promise<any> {
    return await signOut().then(() => {
      this.router.navigateByUrl('/')
      this.authenticationSubject.next(false)
    })
  }

  // Verify that the user is authenticated
  public async isAuthenticated (): Promise<boolean> {
    if (this.authenticationSubject.value) {
      return await Promise.resolve(true)
    } else {
      return await this.getUser()
        .then((user: any) => {
          if (user) {
            return true
          } else {
            return false
          }
        })
        .catch(() => {
          return false
        })
    }
  }

  // Get the current user details
  public async getUser (): Promise<any> {
    return await fetchAuthSession()
  }

  // First step to user login -> authenticate the account ID
  public async authUIStateChange (authState, authData) {}

  // Refresh access tokens
  refreshToken = async () => {
    console.log('---> Refreshing Users Auth Token')
    await fetchAuthSession({ forceRefresh: true })
  }

  // Functionality to update user credentials
  public async updateUser (user): Promise<any> {
    return await updateUserAttributes({ userAttributes: user })
  }

  // When the user signs out or the session times put -> reset the data from cognito and session storage
  public async clearUserDetails () {
    try {
      console.log('Clearing User Details')
      await Cache.removeItem('accountId')
      await Cache.removeItem('accountConfig')
      await Cache.clear()
      await localStorage.clear()
      await sessionStorage.clear()
    } catch (error) {
      console.log('error clearing user details')
    } finally {
      location.reload()
    }
  }

  public async authFederatedSignIn (): Promise<any> {
    console.log('---- Federate Login ---- ')
    try {
      await signInWithRedirect()
    } catch (e) {
      console.log(e)
    }
  }
}
